/**
 *     ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { FormGroup } from '@angular/forms';
import { Injectable } from '@angular/core';
import { WizardSubStepTitlesEnum } from '../../enums';

@Injectable()
export class PortsService {
    /**
     * Format the ports ready for the api based on the port selector value.
     */
    public formatPortsForOutput(form: FormGroup): void {
        const individuals = form.get(WizardSubStepTitlesEnum.GENERAL + '.portIndividuals').disabled
            ? []
            : form.get(WizardSubStepTitlesEnum.GENERAL + '.portIndividuals').value;
        const portRanges = form.getRawValue()[WizardSubStepTitlesEnum.GENERAL]['portRanges'];

        let newPortsValue: string;

        if (form.get(WizardSubStepTitlesEnum.GENERAL + '.enableRanges').value) {
            const formattedRanges = [];
            let filteredIndividuals = individuals;

            for (const range of portRanges) {
                const newIndividuals = this.removeIndividualsInRange(filteredIndividuals, range.portStart, range.portEnd);
                if (newIndividuals && newIndividuals.length) {
                    filteredIndividuals = newIndividuals;
                }
                formattedRanges.push(range.portStart + '-' + range.portEnd);
            }

            newPortsValue = formattedRanges.concat(filteredIndividuals).toString();
        } else {
            newPortsValue = individuals.toString();
        }

        form.get(WizardSubStepTitlesEnum.GENERAL + '.ports').setValue(newPortsValue, { emitEvent: false });
    }

    /**
     * Filters out an individual if it is a range.
     * @param individuals
     * @param portStart
     * @param portEnd
     * @private
     */
    private removeIndividualsInRange(individuals: any[], portStart: number, portEnd: number) {
        return individuals.filter(individual => individual < portStart || individual > portEnd);
    }

    /**
     * Update the ports format.
     */
    public updatePortFormat(ports: any): any[] {
        let formattedPorts: any[];
        if (ports && typeof ports === 'string') {
            if (ports.includes('-') && ports.includes(',')) {
                // If ports are mixed with single ports and ranges or ports are multiple ranges.
                formattedPorts = this.updateMixedPortValues(ports);
            } else if (ports.includes('-')) {
                // If ports are just ranges.
                const splitPorts = ports.split('-');
                formattedPorts = [this.parsePorts(splitPorts)];
            } else {
                // If ports are just single ports or multiple single ports.
                const splitPorts = ports.split(',');
                formattedPorts = this.parsePorts(splitPorts);
            }
        } else if (ports && typeof ports === 'number') {
            return [ports];
        }

        return formattedPorts;
    }

    /**
     * Updated the mixed port format.
     * @private
     */
    private updateMixedPortValues(ports: string): any[] {
        const formattedPorts = [];
        const splitPorts = ports.split(',');

        for (const port of splitPorts) {
            if (!port.includes('-')) {
                formattedPorts.push(parseInt(port, 10));
            } else {
                const portRange = port.split('-');
                formattedPorts.push(this.parsePorts(portRange));

            }
        }

        return formattedPorts;
    }

    /**
     * Parse the ports so they are no longer stings.
     * @param ports
     * @private
     */
    private parsePorts(ports: string[]): any[] {
        const parsedPorts = [];
        for (const port of ports) {
            parsedPorts.push(parseInt(port, 10));
        }

        return parsedPorts;
    }
}
